import {
  Background,
  FieldTypeAsset,
  FieldTypeHTML,
  FieldTypeLink,
  ImagePosition,
} from '@allesauto/utils/consts';
import {
  createMarkup,
  getUrlFromStoryblokLink,
  parseSlashToURL,
} from '@allesauto/utils/funcs';
import {
  ITypeListMarker,
  ITypeListMarkerColor,
} from '@allesauto/utils/storyblok';
import { renderRichText } from '@storyblok/react';
import { sbEditable } from '@storyblok/storyblok-editable';
import clsx from 'clsx';
import React from 'react';

import NextImage from '../nextImage';
import TextLink from '../textLink';

export interface ImageTextStepProps {
  image: FieldTypeAsset;
  headline: string;
  text: FieldTypeHTML | string;
  number: string;
  imagePosition?: ImagePosition;
  background?: Background;
  textLinkText?: string;
  textLinkHref?: FieldTypeLink;
  listItemMarker?: ITypeListMarker;
  listItemMarkerColor?: ITypeListMarkerColor;
  component: string;
  _uid: string;
  _editable?: string;
}

const ImageTextStep: React.FC<ImageTextStepProps> = (
  props: ImageTextStepProps
) => {
  const {
    image,
    headline,
    text,
    number,
    imagePosition = ImagePosition.LEFT,
    background = Background.WHITE,
    textLinkText,
    textLinkHref,
    listItemMarker,
    listItemMarkerColor,
  } = props;

  const bg = background in Background ? background : Background.WHITE;

  const themes = {
    PURPLE: {
      container: 'bg-purple80 monochrome:bg-black wl-bg-highlight',
      headline: 'text-white monochrome:text-white wl-text-highlight',
      text: 'text-white monochrome:text-white wl-text-highlight',
    },
    GREY: {
      container: 'bg-grey10 image-text-step-grey-bg',
      headline: 'text-grey100',
      text: 'text-grey80',
    },
    WHITE: {
      container: 'bg-white',
      headline: 'text-grey100',
      text: 'text-grey80',
    },
  };

  const className = {
    container: clsx(
      'auto-rows-auto gap-y-40px grid grid-cols-24 py-30px lg:py-60px',
      themes[bg].container
    ),
    image: clsx(
      'aspect-[16/10] col-end-24 col-start-2 relative row-end-2 row-start-1',
      imagePosition === ImagePosition.LEFT && 'lg:col-end-12',
      imagePosition !== ImagePosition.LEFT && 'lg:col-start-14'
    ),
    numberContainer: clsx(
      'h-min row-end-2 row-start-1 z-10',
      imagePosition === ImagePosition.LEFT &&
        'col-start-20 col-end-24 lg:col-start-10 lg:col-end-12',
      imagePosition !== ImagePosition.LEFT &&
        'col-start-2 lg:col-start-14 lg:col-end-16 col-end-6'
    ),
    number: clsx(
      'font-headline h-2/3 pt-10px text-center text-headline1 lg:pt-20px xl:pb-20px ',
      'bg-green60 text-purple80',
      'monochrome:bg-black monochrome:text-white',
      'wl-bg-highlight wl-text-highlight'
    ),
    textContainer: clsx(
      'col-end-24 col-start-2',
      imagePosition === ImagePosition.LEFT && 'lg:col-start-14',
      imagePosition !== ImagePosition.LEFT && 'lg:col-end-12'
    ),
    headline: clsx(
      'font-headline mb-20px text-headline2 lg:mb-40px lg:mt-30px',
      themes[bg].headline
    ),
    text: clsx(
      '!leading-[22px] font-regular rich-text space-y-20px text-p1 lg:!leading-6',
      themes[bg].text,
      `rich-text-list-marker-${listItemMarker ? listItemMarker : 'bullet'}-${
        listItemMarkerColor ? listItemMarkerColor : 'purple'
      }`
    ),
    textLink: 'mt-20px',
  };

  const url = textLinkHref && getUrlFromStoryblokLink(textLinkHref);
  const imageTextStepLink = (url && parseSlashToURL(url)) || '';

  return (
    <div {...sbEditable(props)} className={className.container}>
      <div className={className.numberContainer}>
        <div className={className.number}>{number}</div>
      </div>
      <div className={className.image}>
        <NextImage
          baseUrls={
            process.env.IMAGE_BASE_URL ||
            process.env.NEXT_PUBLIC_IMAGE_BASE_URL ||
            ''
          }
          alt={image.alt ?? ''}
          layout="fill"
          src={image.filename ?? null}
          objectFit="cover"
        />
      </div>
      <div className={className.textContainer}>
        <h2 className={className.headline}>{headline}</h2>
        <div
          className={className.text}
          {...(text && {
            dangerouslySetInnerHTML: createMarkup(
              (text as unknown as FieldTypeHTML).type
                ? renderRichText(text as FieldTypeHTML)
                : (text as unknown as string)
            ),
          })}
        />
        {textLinkText && (
          <TextLink
            text={textLinkText}
            href={imageTextStepLink}
            target={textLinkHref?.target ?? '_self'}
            customClassName={className.textLink}
            isDarkBg={bg === Background.PURPLE}
          />
        )}
      </div>
    </div>
  );
};

export default ImageTextStep;
